import React from 'react'
import { SquarePen, SlidersHorizontal } from 'lucide-react'
import { Button } from 'components/ui/button'
import { SearchInput } from 'components/SearchInput'
import { cn } from 'lib/utils'

interface NotesWorkspaceHeaderProps {
	title: string
	subtitle: string
	/** Notes matching the current search and filters. */
	visibleCount: number
	totalCount: number
	search: string
	onSearchChange: (value: string) => void
	onNewNote: () => void
	/** Opens the rail in a Sheet. Only rendered below xl. */
	onOpenFilters: () => void
	/** Shows a dot on the Filters button when something is narrowing the feed. */
	hasActiveFilters: boolean
	/** False on an empty surface, where there is nothing to filter. */
	showFilters: boolean
	className?: string
}

/**
 * The page header.
 *
 * One header for every surface. The three feeds this replaced had each grown
 * their own, with their own rule about when it was even visible. Here it is
 * always present, so search and the composer never disappear.
 */
const NotesWorkspaceHeader: React.FC<NotesWorkspaceHeaderProps> = ({
	title,
	subtitle,
	visibleCount,
	totalCount,
	search,
	onSearchChange,
	onNewNote,
	onOpenFilters,
	hasActiveFilters,
	showFilters,
	className,
}) => {
	const isNarrowed = visibleCount !== totalCount

	return (
		<div className={cn('flex flex-wrap items-start justify-between gap-4', className)}>
			<div className="flex min-w-0 flex-col gap-1">
				<h2 className="m-0 text-2xl font-bold text-foreground">{title}</h2>
				<span className="text-sm text-muted-foreground">
					{subtitle}
					{totalCount > 0 && (
						<>
							{' · '}
							<span className="tabular-nums">
								{isNarrowed ? `${visibleCount} of ${totalCount}` : totalCount}
							</span>{' '}
							{totalCount === 1 && !isNarrowed ? 'note' : 'notes'}
						</>
					)}
				</span>
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<SearchInput
					value={search}
					onChange={onSearchChange}
					placeholder="Search notes..."
					className="w-full sm:w-64"
				/>

				{/* The rail is a column at xl and up, so this only exists below it. */}
				{showFilters && (
					<Button
						variant="ghost"
						onClick={onOpenFilters}
						className="relative xl:hidden"
						aria-label="Open filters"
					>
						<span className="flex items-center gap-2">
							<SlidersHorizontal className="h-4 w-4" />
							Filters
						</span>
						{hasActiveFilters && (
							<span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-primary" />
						)}
					</Button>
				)}

				<Button variant="primary" onClick={onNewNote}>
					<span className="flex items-center gap-2">
						<SquarePen className="h-4 w-4" />
						New Note
					</span>
				</Button>
			</div>
		</div>
	)
}

export default NotesWorkspaceHeader
